import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaChevronDown, FaCalendarAlt, FaMapMarkerAlt, FaArrowRight, FaQuoteLeft } from 'react-icons/fa';
import { EVENT } from '../data/eventInfo';
import { TESTIMONIES } from '../data/testimonies';
import { useSiteContent } from '../data/useSiteContent';
import { useAuth } from '../auth/AuthContext';
import EditableText from '../components/EditableText';
import Gallery from '../components/Gallery';
import RegisterCTA from '../components/RegisterCTA';
import GetReminded from '../components/GetReminded';

const heroImages = [
  '/flowers.jpg',
  '/bik.jpg',
  '/lake_walk.jpg',
  '/bible_rings.jpg'
];

export default function Home() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const preloadedImages = useRef([]);
  const { content, updateKey } = useSiteContent();
  const { isAdmin } = useAuth();

  useEffect(() => {
    let loadedCount = 0;
    preloadedImages.current = heroImages.map((src) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        loadedCount++;
        if (loadedCount === heroImages.length) setImagesLoaded(true);
      };
      return img;
    });
  }, []);

  useEffect(() => {
    if (!imagesLoaded) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((i) => (i === heroImages.length - 1 ? 0 : i + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [imagesLoaded]);

  const primaryTestimonies = TESTIMONIES.filter(t => t.primary);

  return (
    <>
      {/* Hero */}
      <section className="hero">
        {heroImages.map((image, index) => (
          <div
            key={image}
            className={`hero-image ${imagesLoaded && index === currentImageIndex ? 'active' : ''}`}
            style={{ backgroundImage: `url(${image})` }}
          />
        ))}
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <p className="hero-presents">West Coast Ministries presents</p>
          <h1>
            <span className="nowrap">West Coast</span>{' '}
            <span className="nowrap">Marriage Camp</span>
          </h1>
          <p className="hero-tagline">A husband and wife, walking with God together.</p>
          <a href="#dates" className="cta-button">Learn More</a>
        </div>
        <div className="scroll-indicator">
          <FaChevronDown className="chevron" />
          <FaChevronDown className="chevron" />
          <FaChevronDown className="chevron" />
        </div>
      </section>

      {/* Date + Location + Register (fast path) */}
      <section id="dates" className="section dates-band">
        <div className="container">
          <div className="dates-content">
            <div className="dates-info">
              <div className="dates-line">
                <FaCalendarAlt className="dates-icon" />
                <EditableText
                  value={content.event_date_display}
                  canEdit={isAdmin}
                  onSave={(v) => updateKey('event_date_display', v)}
                />
              </div>
              <div className="dates-line">
                <FaMapMarkerAlt className="dates-icon" />
                <EditableText
                  value={content.event_location}
                  canEdit={isAdmin}
                  onSave={(v) => updateKey('event_location', v)}
                />
              </div>
            </div>
            <div className="dates-cta">
              <RegisterCTA className="cta-button cta-register" />
              {!EVENT.registrationOpen && <GetReminded />}
            </div>
          </div>
        </div>
      </section>

      {/* "Your marriage is worth it" intro */}
      <section className="section intro">
        <div className="container narrow">
          <h2>Your Marriage is Worth It</h2>
          <div className="intro-body">
            <p>
              Whether you've been married for months, years, or decades, having the right tools,
              training, and orientation is crucial to a redemptive marriage.
            </p>
            <p>
              Join us, as you walk together with God, and discover and uncover the marriage
              He has intended and designed for you both.
            </p>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section experience">
        <div className="container">
          <h2>What to Expect</h2>
          <p className="subtitle">A weekend designed around your heart, your marriage, and the larger story God desires for your life.</p>
          <div className="experience-content">
            <div className="experience-item">
              <FaHeart className="exp-icon" />
              <div>
                <h3>Connection</h3>
                <p>Guided sessions that move past surface-level communication and into true intimacy — knowing and being known, by God and by your spouse.</p>
              </div>
            </div>
            <div className="experience-item">
              <FaHeart className="exp-icon" />
              <div>
                <h3>Time Away, Together</h3>
                <p>Uninterrupted time with your spouse, away from the noise. Rediscover why you fell in love.</p>
              </div>
            </div>
            <div className="experience-item">
              <FaHeart className="exp-icon" />
              <div>
                <h3>Become Equipped for Your Journey</h3>
                <p>Gain the tools, training, and orientation you carry home with you — practical ways to keep walking with God together long after the weekend ends.</p>
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/our-mission" className="text-link">
              Read about our mission and approach <FaArrowRight style={{ marginLeft: '0.3rem', fontSize: '0.85rem' }} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonies preview */}
      <section className="section testimonies-preview">
        <div className="container">
          <h2>Real Stories from Real Marriages</h2>
          <p className="subtitle">Personal impacts and experiences from couples.</p>
          <div className="testimony-grid">
            {primaryTestimonies.map(t => (
              <div key={t.id} className="testimony-card">
                <FaQuoteLeft className="quote-icon" />
                <p className="testimony-quote">{t.quote}</p>
                <p className="testimony-name">— {t.name}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/testimonies" className="cta-button cta-secondary">
              Read More Testimonies <FaArrowRight style={{ marginLeft: '0.4rem' }} />
            </Link>
          </div>
        </div>
      </section>

      <Gallery />

      {/* Come Walk With Us + Support */}
      <section className="section register">
        <div className="container">
          <h2 className="register-heading">Come Walk With Us</h2>
          <p className="subtitle">Join us at camp, or become an ongoing sponsor for transforming marriages.</p>
          <div className="cta-group">
            <RegisterCTA className="cta-button" label="Sign Up Today" arrow={false} />
            <a href={EVENT.donateUrl} target="_blank" rel="noopener noreferrer" className="cta-button cta-outline">
              Donate
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
